/**
 * "Email" action for an invoice or a customer statement (tracker item 35):
 * a confirm dialog naming the recipient, then queues the message (a signed,
 * expiring view link; no attachment). Shows the document's last send status
 * and any error. ADMIN and OFFICE_MANAGER only (backend requireRole).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createAPIClient, INotificationsApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { secondaryButtonClass } from './formStyles';
import { OutboxStatusBadge } from './NotificationBadges';
import { messageStatusText, newIdempotencyKey } from '../pages/settings/notificationUi';
import { Toast } from './Toast';

interface EmailDocumentButtonProps {
  kind: 'invoice' | 'statement';
  /** Invoice id, or customer id for a statement */
  id: string;
  /** Where it will go (the customer's email); null when the customer has none */
  recipient: string | null | undefined;
  /** e.g. "invoice #1042" or "the statement for ACME" */
  documentLabel: string;
  /** Why emailing is not possible right now (button disabled), if anything */
  disabledReason?: string | null;
}

export function EmailDocumentButton({ kind, id, recipient, documentLabel, disabledReason }: EmailDocumentButtonProps) {
  const api: INotificationsApi = useMemo(() => createAPIClient(), []);
  const [last, setLast] = useState<types.OutboxMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const sourceType = kind === 'invoice' ? 'INVOICE' : 'STATEMENT';

  const loadLast = useCallback(async () => {
    const page = await api.listOutbox({ sourceType, sourceId: id }, 1, 1);
    setLast(page.data[0] ?? null);
  }, [api, sourceType, id]);

  useEffect(() => {
    loadLast().catch(() => setLast(null));
  }, [loadLast]);

  async function send() {
    if (!recipient) return;
    if (!window.confirm(`Email ${documentLabel} to ${recipient}? The message contains a link to view it online for 30 days.`)) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await (kind === 'invoice' ? api.emailInvoice(id, newIdempotencyKey()) : api.emailStatement(id, newIdempotencyKey()));
      setLast(result.message);
      setNotice(
        result.deduplicated
          ? 'This was already queued a moment ago.'
          : result.message.status === 'SKIPPED'
            ? `Not sent: ${result.message.skipReasonText ?? result.message.skipReason}`
            : `Queued to ${result.message.toAddress}.`
      );
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not email this document'));
    } finally {
      setBusy(false);
    }
  }

  const blocked = disabledReason || (!recipient ? 'The customer has no email address.' : null);
  return (
    <div className="flex flex-col items-start gap-1" data-testid={`email-${kind}`}>
      <button type="button" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={busy || !!blocked} title={blocked ?? undefined} onClick={() => void send()}>
        Email
      </button>
      {blocked && <span className="text-xs text-gray-500">{blocked}</span>}
      {last && (
        <span className="text-xs text-gray-600 flex flex-wrap items-center gap-1">
          Last email: <OutboxStatusBadge status={last.status} /> {new Date(last.createdAt).toLocaleString()}
          {(last.status === 'SKIPPED' || last.status === 'FAILED' || last.deliveryStatus) && <span>· {messageStatusText(last)}</span>}
        </span>
      )}
      {notice && <Toast kind="success">{notice}</Toast>}
      {error && <Toast kind="error">{error}</Toast>}
    </div>
  );
}
